import { Component, ViewEncapsulation, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { tap } from 'rxjs';

import { SeatMapFacade } from '@seat-map/seat-map.facade';
import { TripTypes, AirValidationFlightsSector } from '@seat-map/entities/itinerary-detail';
import { MultiplePassengersSeats, PassengersSeats } from '@seat-map/entities/seat-map';
import { Travelers } from '@booking/entities/booking-arrangement';
import { AppFacade } from '@app/app.facade';
import { SeatMapAbstract } from '@seat-map/components/abstracts/seat-map.abstract';
import { ReservationType } from '@user-history/entities/user-history-item';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { AvoService } from '@avo/services/avo.service';

@Component({
  selector: 'app-seat-map-mobile',
  templateUrl: './seat-map-mobile.component.html',
  styleUrls: ['./seat-map-mobile.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SeatMapMobileComponent extends SeatMapAbstract implements OnInit, OnDestroy {

  constructor(
    protected facade: SeatMapFacade,
    protected appFacade: AppFacade,
    protected router: Router,
    protected domManiputaleService: DomManipulateService,
    protected translateService: TranslocoService,
    protected mobileDeviceService: MobileDeviceService,
    protected avoService: AvoService,
  ) {
    super(facade, appFacade, router,
      domManiputaleService, translateService, mobileDeviceService, avoService);
  }

  ngOnInit(): void {
    this.subscriptions.push(this.itineraryDetail$.subscribe(itinerary => {
      this.itineraryDetail = itinerary;
    }));

    this.subscriptions.push(this.showError$.subscribe(showError => {
      this.showError = showError;
      this.registerAmplitudeEventErrors();
    }));

    this.subscriptions.push(this.seatMapError$.subscribe(seatMapError => {
      this.seatMapErrors = seatMapError;
    }));

    this.subscriptions.push(this.seatMapTransactions$.subscribe(transactions => {
      this.seatMapTransactions = transactions;
    }));

    this.domManiputaleService.setBodyScroll(true);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  public emptySeats(passengerSeats: PassengersSeats[]): boolean {
    return passengerSeats && passengerSeats.length > 0 ? passengerSeats.filter(x => x.SeatNumber).length === 0 : true;
  }

  public onePassenger(passengers: Travelers[]): boolean {
    return passengers && passengers.length === 1 ? true : false;
  }

  public sectorType(sectorNumber: number): string {
    return `FLIGHT_RESULTS_MODULE.ITINERARY_LIST_SECTION.${sectorNumber === TripTypes.DEPARTURE ?
      'DEPARTURE_TRIP' : 'RETURN_TRIP'}`;
  }

  public toggleSelectSector(): void {
    this.showSelectSector = !this.showSelectSector;
  }

  public showArrowRight(flight: AirValidationFlightsSector, sectors: AirValidationFlightsSector[]): boolean {
    return sectors[sectors.length - 1].Number !== flight.Number;
  }

  public goBack(userId: string): void {
    if (this.itineraryDetail) {
      this.router.navigate(['user-history', 'detail', userId, this.itineraryDetail.Id, ReservationType.Flight]);
    }
  }

  public removeSeatInFlight(seatNumber: string, passengersNumber: number): void {
    this.avoService.getEventTriggerProps$().pipe(
      tap(trigger => {
        return this.AvoDispatch.selectSeatmapSeatRemove({
          ...this.avoService.avoBaseProperties(trigger),
          seatRemoved: seatNumber
        });
      }
      )).subscribe().unsubscribe();
    this.facade.removeSeatInFlightAction(seatNumber);
    if (passengersNumber !== null) {
      this.calculateDetailSeats(passengersNumber);
    }
  }

  public enableConfirm(passengersSeats: PassengersSeats[]): boolean {
    return !!passengersSeats && passengersSeats.filter(seat => !seat.SeatNumber).length === 0;
  }

  public filterSeatsSelectedMultiplePassengers(passengersSeats: PassengersSeats[]): MultiplePassengersSeats[] {
    const seats = passengersSeats;
    const seatsMultiPassengers: MultiplePassengersSeats[] = [];
    if (seats && seats.length > 0) {
      seatsMultiPassengers.push({
        Name: seats[0].FirstName[0] + seats[0].Surname[0],
        SeatNumber: seats[0].SeatNumber,
        TotalNumber: seats.length - 2 > 0 ? seats.length - 2 : null
      } as MultiplePassengersSeats);
    }
    if (seats && seats.length > 1) {
      seatsMultiPassengers.push({
        Name: seats[1].FirstName[0] + seats[1].Surname[0],
        SeatNumber: seats[1].SeatNumber,
        TotalNumber: seats.length - 2 > 0 ? seats.length - 2 : null
      } as MultiplePassengersSeats);
    }
    if (this.showDetailSeats) {
      this.calculateDetailSeats(seats.length);
    }
    return seatsMultiPassengers;
  }

  public openDetailSeats(passengersNumber: number): void {
    this.showDetailSeats = !this.showDetailSeats;
    if (this.showDetailSeats) {
      this.calculateDetailSeats(passengersNumber);
    }
  }

  private calculateDetailSeats(passengersNumber: number): void {
    if (passengersNumber === 0) {
      this.closeDetailSeat();
    } else {
      this.heightDetailSeats = (passengersNumber * this.heightItemSeat) + 20;
      this.topDetailSeats = passengersNumber * this.marginItemSeat * -1;
    }
  }

  public closeDetailSeat(): void {
    this.showDetailSeats = !this.showDetailSeats;
    this.heightDetailSeats = this.heightItemSeatDefault;
    this.topDetailSeats = 0;
  }
}
